import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faEye,
  faShieldHalved,
  faLightbulb,
  faHandshake,
  faArrowsSpin,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../components/Footer';
import './About.css';
import founderImg from '../assets/founder.png';
import cofounderImg from '../assets/team2.jpeg';
import cyberSec from '../assets/cybersecLead.jpeg';
import aboutImg from '../assets/aboutImg.png';

const teamMembers = [
  {
    id: 1,
    name: "Stephen Oduor",
    role: "Founder & Lead Architect",
    image: founderImg,
    bio: "Pioneering enterprise platforms, scalable full-stack web architectures, secure cloud infrastructure, automated payment gateways, and proactive penetration testing.",
    skills: ["React", "Django", "Node.js", "PHP", "Penetration Testing", "SQL / NoSQL"]
  },
  {
    id: 2,
    name: "Shann Bahati",
    role: "Database Specialist & Lead UI/UX Engineer",
    image: cofounderImg,
    bio: "Designing high-performance relational and document schemas, tuning query performance, and crafting design systems to deliver intuitive digital experiences.",
    skills: ["Database Design", "SQL/NoSQL Optimization", "UI/UX Systems", "Wireframing & Prototyping"]
  },
  {
    id: 3,
    name: "Lewis Mugambi",
    role: "Cybersecurity Lead & Forensics Engineer",
    image: cyberSec,
    bio: "Hardening infrastructure through threat modeling, ethical penetration testing, vulnerability assessments, and digital forensics to secure business operations.",
    skills: ["Penetration Testing", "Digital Forensics", "Vulnerability Assessment", "Incident Response"]
  }
];

const valuesList = [
  {
    number: "01",
    title: "Integrity & Reliability",
    tagline: "Uncompromising engineering standards",
    desc: "We build systems with transparent architectures, zero shortcuts, and reliable uptime when operations demand it most.",
    icon: faShieldHalved
  },
  {
    number: "02",
    title: "Relentless Innovation",
    tagline: "Modern architectures for evolving demands",
    desc: "From full-stack web platforms to M-Pesa automated APIs, we deploy current tooling to eliminate business friction.",
    icon: faLightbulb
  },
  {
    number: "03",
    title: "Customer Focus",
    tagline: "Strategic alignment with your goals",
    desc: "We co-engineer directly with founders and teams to solve root problems, streamline workflows, and accelerate growth.",
    icon: faHandshake
  },
  {
    number: "04",
    title: "Continuous Learning",
    tagline: "Constant audits and iteration",
    desc: "We actively benchmark code, harden security, and adopt better patterns to ensure high-performance deployments.",
    icon: faArrowsSpin
  }
];

const About = () => {
  useEffect(() => {
    const animatedElements = document.querySelectorAll(".reveal-on-scroll");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -30px 0px",
      }
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="about-page-wrapper">
      <div className="about-page">
        {/* Header Section */}
        {/* <header className="about-header reveal-on-scroll slide-up">
          <span className="about-badge">About Our Organization</span> 
          <h1>Engineered for Scale, <span>Driven by Excellence</span></h1>
          <p>
            Stemiot Softwares is a digital engineering firm building custom web platforms,
            database architectures, and automated systems for modern organizations.
          </p>
        </header> */}

        {/* Who We Are Section */}
        <section className="who-we-are-section">
          <div className="who-grid">
            <div className="who-text-pane reveal-on-scroll slide-left">
              <span className="sub-badge">Identity & Purpose</span>
              <h2>Who We Are</h2>
              <p className="lead-text">
                We transform operational bottlenecks into reliable, secure, and scalable digital solutions.
              </p>
              <p>
                We serve startups, growing businesses, and enterprises that require more than generic tools. 
                Our team designs custom full-stack web applications, optimized database layers, mobile integrations, 
                and secure hosting environments tailored to operational workflows.
              </p>
              <p>
                Whether integrating real-time payment solutions or building administration portals, we build 
                systems that operate dependably without technical ceilings.
              </p>
            </div>

            <div className="who-image-frame">
              <img 
                src={aboutImg} 
                alt="Stemiot Softwares Engineers Collaborating" 
                loading="lazy" 
              />
              <div className="who-experience-card">
                <span className="exp-number">100%</span>
                <span className="exp-label">Production-Grade Engineering</span>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision Section */}
        <section className="about-grid">
          <div className="about-card reveal-on-scroll slide-left">
            <div className="about-card-header">
              <div className="card-icon-wrap mission-icon">
                <FontAwesomeIcon icon={faBullseye} />
              </div>
              <h2>Our Mission</h2>
            </div>
            <p>
              To deliver secure, reliable, and custom software solutions that simplify complex operations, 
              protect digital assets, and drive business growth.
            </p>
          </div>

          <div className="about-card reveal-on-scroll slide-right">
            <div className="about-card-header">
              <div className="card-icon-wrap vision-icon">
                <FontAwesomeIcon icon={faEye} />
              </div>
              <h2>Our Vision</h2>
            </div>
            <p>
              To be a trusted engineering partner recognized for architectural discipline, resilient code, 
              and intuitive interfaces across regional and global markets.
            </p>
          </div>
        </section>

        {/* Core Values Section (Clean 4-Card Grid, No Extra Images) */}
        <section className="about-values">
          <div className="section-title reveal-on-scroll slide-up">
            {/* <span className="sub-badge">Guiding Standards</span> */}
            <h2>Our Core <span>Values</span></h2>
            <p>
              The foundational principles guiding how we collaborate with clients, write code, and deliver every project.
            </p>
          </div>

          <div className="values-clean-grid">
            {valuesList.map((val, index) => (
              <div 
                key={index} 
                className="value-clean-card reveal-on-scroll pop-in"
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className="value-card-top">
                  <div className="value-icon-badge">
                    <FontAwesomeIcon icon={val.icon} />
                  </div>
                  <span className="value-number-label">{val.number}</span>
                </div>
                <h3>{val.title}</h3>
                <span className="value-tagline">{val.tagline}</span>
                <p>{val.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Team / Leadership Section */}
        <section className="about-team">
          <div className="section-title reveal-on-scroll slide-up">
            <span className="sub-badge">Technical Leadership</span>
            <h2>Leadership & <span>Engineering</span></h2>
            
          </div>

          <div className="team-row-grid">
            {teamMembers.map((member, index) => (
              <article 
                className="team-card-row reveal-on-scroll pop-in" 
                key={member.id}
                style={{ transitionDelay: `${index * 110}ms` }}
              >
                <div className="team-card-image-box">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    loading="lazy"
                  />
                </div>
                <div className="team-card-text-box">
                  <h3 className="team-member-name">{member.name}</h3>
                  <p className="team-member-title">{member.role}</p>
                  <p className="team-member-bio">{member.bio}</p>
                  <div className="team-tags">
                    {member.skills.map((skill, sIdx) => (
                      <span className="tag" key={sIdx}>{skill}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="about-cta reveal-on-scroll pop-in">
          {/* <h2>Ready to Architect Your Next Solution?</h2> */}
          <p>Let's collaborate to build scalable, secure, and high-impact digital systems for your organization.</p>
          <Link to="/contact" className="btn-primary">Get Started</Link>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default About;